import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import './AuthPages.css';

const AdminSignup = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    adminCode: '',
    masterKey: '',
    department: '',
    employeeId: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAdminCode, setShowAdminCode] = useState(false);
  const [showMasterKey, setShowMasterKey] = useState(false);
  
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (currentUser) {
      if (currentUser.role === 'ADMIN') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [currentUser, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    console.log('🔍 Validating form...');
    console.log('📝 Current form data:', formData);
    
    if (formData.password !== formData.confirmPassword) {
      console.log('❌ Password mismatch');
      setError('Passwords do not match');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }

    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }

    if (!/[0-9]/.test(formData.password)) {
      setError('Password must contain at least one number');
      return false;
    }

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return false;
    }

    if (!formData.masterKey) {
      console.log('❌ Master key missing');
      setError('Master key is required for admin registration');
      return false;
    }

    if (!formData.adminCode) {
      setError('Admin access code is required');
      return false;
    }

    console.log('✅ Form validation passed');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔍 Admin Signup: Form submitted');
    console.log('📝 Form data:', formData);
    
    setLoading(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      console.log('❌ Form validation failed');
      setLoading(false);
      return;
    }

    console.log('✅ Form validation passed');

    try {
      // Prepare admin signup data for API using regular signup endpoint
      const adminSignupData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: 'ADMIN',
        businessName: formData.department,
        businessType: 'ADMINISTRATION',
        phone: formData.employeeId,
        adminCode: formData.adminCode,
        masterKey: formData.masterKey,
        firstName: formData.fullName.split(' ')[0] || formData.fullName,
        lastName: formData.fullName.split(' ').slice(1).join(' ') || '',
        department: formData.department
      };

      console.log('📊 Admin signup data prepared:', adminSignupData);
      console.log('🔑 Admin code being sent:', formData.adminCode);

      // Call the auth signup endpoint with admin role
      const response = await api.post('/api/auth/signup', adminSignupData);
      const result = response.data;

      if (result.success) {
        console.log('✅ Admin created successfully:', result.data);
        setSuccess('Admin account created successfully! You can now login.');
        
        // Clear form
        setFormData({
          fullName: '',
          email: '',
          username: '',
          password: '',
          confirmPassword: '',
          adminCode: '',
          masterKey: '',
          department: '',
          employeeId: ''
        });

        console.log('✅ Form cleared, redirecting in 2 seconds...');

        // Redirect to login after 2 seconds
        setTimeout(() => {
          console.log('🔄 Redirecting to admin login...');
          navigate('/secure-admin-portal/login');
        }, 2000);
      } else {
        console.log('❌ API Error:', result.message);
        setError(result.message || 'Failed to create admin account');
      }

    } catch (error) {
      console.error('❌ Error during admin signup:', error);
      setError(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create admin account. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card admin-signup-card">
        <div className="auth-header">
          <div className="auth-logo-text">
            <img src="/images/clickpay_logo.png" alt="ClickPay - Secure Payment Gateway" className="auth-logo" />
          </div>
          <h1>Admin Registration</h1>
          <p>Create new administrator account</p>
        </div>
        
        {error && (
          <div className="alert alert-danger" role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                className="form-control"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="username">Admin Username</label>
              <input
                type="text"
                id="username"
                name="username"
                className="form-control"
                value={formData.username}
                onChange={handleChange}
                placeholder="Choose admin username"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="employeeId">Employee ID</label>
              <input
                type="text"
                id="employeeId"
                name="employeeId"
                className="form-control"
                value={formData.employeeId}
                onChange={handleChange}
                placeholder="Enter employee ID"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="department">Department</label>
            <select
              id="department"
              name="department"
              className="form-control"
              value={formData.department}
              onChange={handleChange}
              required
            >
              <option value="">Select Department</option>
              <option value="IT">Information Technology</option>
              <option value="FINANCE">Finance</option>
              <option value="OPERATIONS">Operations</option>
              <option value="COMPLIANCE">Compliance</option>
              <option value="CUSTOMER_SUPPORT">Customer Support</option>
              <option value="MANAGEMENT">Management</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-wrapper">
              <input
               type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                className="form-control"
                value={formData.password}
                onChange={handleChange}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                placeholder="Create password (min 8 chars)"
                required
              />
              <span onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
              </span>
            </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                className="form-control"
                value={formData.confirmPassword}
                onChange={handleChange}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                placeholder="Confirm password"
                required
              />
               <span onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
               </span>
            </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="adminCode">Admin Access Code</label>
           <div className="password-wrapper">
            <input
              type={showAdminCode ? "text" : "password"}
              id="adminCode"
              name="adminCode"
              className="form-control"
              value={formData.adminCode}
              onChange={handleChange}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              placeholder="Enter admin access code"
              required
            />
            <span onClick={() => setShowAdminCode(!showAdminCode)}>
               {showAdminCode ? <FiEye size={18} /> : <FiEyeOff size={18} />}
            </span>
            </div>
            <small className="form-text">
              Security code required for admin access
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="masterKey">Master Key</label>
            <div className="password-wrapper">
            <input
              type={showMasterKey ? "text" : "password"}
              id="masterKey"
              name="masterKey"
              className="form-control"
              value={formData.masterKey}
              onChange={handleChange}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              placeholder="Enter master key"
              required
            />
            <span onClick={() => setShowMasterKey(!showMasterKey)}>
                {showMasterKey ? <FiEye size={18} /> : <FiEyeOff size={18} />}
            </span>
           </div>
            <small className="form-text">
              Master key required for admin registration
            </small>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-full admin-btn"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Admin Account'}
          </button>
        </form>

        <div className="auth-links">
          <p>
            Already have an admin account? <Link to="/secure-admin-portal/login">Login here</Link>
          </p>
        </div>

        <div className="security-notice">
          <i className="fas fa-shield-alt"></i>
          <p>Admin registration requires proper authorization. All registrations are logged and monitored.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminSignup;